import { useParams } from "react-router-dom";
import { Mail, Phone, Check, Circle, ArrowDown, FileQuestion } from "lucide-react";
import { absoluteUrl } from "@/utils/common.utils";
import { PHASE_LABELS, PHASES_FOR } from "@/utils/deals.utils";
import PublicMicrositeSkeleton from "@/components/public/skeletons/PublicMicrositeSkeleton";
import EmptyState from "@/components/common/EmptyState";
import { useGetPublicDeal } from "@/hooks/useDeals";
import { DealType } from "@/enum/common.enum";

export default function PublicMicrosite() {
  const { slug } = useParams();
  const { data, error: err } = useGetPublicDeal(slug ?? "");

  if (err) return <EmptyState icon={FileQuestion} title="This page is not available" description="The owner may have turned sharing off, or the link may no longer be active." className="mx-auto mt-24 max-w-lg" />;
  if (!data) return <PublicMicrositeSkeleton />;

  const a = data.agent;
  const PHASES: readonly string[] = PHASES_FOR[data.deal_type === DealType.BUY ? DealType.BUY : DealType.SELL];
  const currentPhaseIdx = PHASES.indexOf(data.phase);

  return (
    <div className="min-h-screen bg-linen text-ink">
      {/* HERO */}
      <section className="relative">
        <div className="absolute top-6 left-6 lg:top-8 lg:left-12 z-10 flex items-center gap-2" data-testid="brand-mark">
          <img src="/logo.svg" alt="Stoop" className="h-7 w-auto lg:h-8" />
        </div>
        <div className="grid lg:grid-cols-12 min-h-[80vh] lg:min-h-screen border-b border-edge">
          {/* Without a cover photo the text takes the whole hero. A stock house
              here read as *their* house to whoever opened the link. */}
          <div className={`${data.hero_image_url ? "lg:col-span-7" : "lg:col-span-12"} flex flex-col justify-center p-8 lg:p-20 order-2 lg:order-1`}>
            <div className="overline mb-6" data-testid="microsite-overline">A private update for {data.client_name}</div>
            <h1 className="font-display font-bold text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-tight" data-testid="microsite-address">
              {data.address}
            </h1>
            {data.welcome_message && (
              <p className="mt-8 text-lg text-stone leading-relaxed max-w-xl whitespace-pre-wrap" data-testid="microsite-welcome">
                {data.welcome_message}
              </p>
            )}
            <div className="mt-12 flex items-center gap-3 text-stone text-sm">
              <ArrowDown className="w-4 h-4 animate-bounce" />
              <span>scroll for your timeline</span>
            </div>
          </div>
          {data.hero_image_url && (
            <div className="lg:col-span-5 relative order-1 lg:order-2 min-h-[280px] lg:min-h-screen bg-bone">
              <img
                src={absoluteUrl(data.hero_image_url)}
                alt={`Photo of ${data.address}`}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-primary/15" />
            </div>
          )}
        </div>
      </section>

      {/* PROGRESS */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-12 py-24">
        <div className="overline mb-4">Where we are</div>
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <h2 className="font-display text-5xl tracking-tight" data-testid="microsite-phase">{PHASE_LABELS[data.phase]}</h2>
            <p className="text-stone mt-3 text-sm">{data.tasks_complete} of {data.tasks_total} steps complete</p>
          </div>
          <div className="lg:col-span-5">
            <div className="flex items-baseline justify-end gap-2">
              <span className="font-display text-7xl tracking-tight" data-testid="microsite-progress-pct">{data.progress_pct}</span>
              <span className="overline">%</span>
            </div>
          </div>
        </div>
        <div className="h-2 bg-bone mt-10 overflow-hidden">
          <div className="h-full bg-moss transition-all" style={{ width: `${data.progress_pct}%` }} data-testid="microsite-progress-bar" />
        </div>

        {/* Phase tracker */}
        <div className="mt-10 grid grid-cols-4 gap-px bg-edge border border-edge">
          {PHASES.map((p, i) => {
            const active = i <= currentPhaseIdx;
            return (
              <div key={p} className={`p-5 ${active ? "bg-linen" : "bg-bone"}`} data-testid={`phase-marker-${p}`}>
                <div className={`overline ${active ? "text-moss" : "text-stone"}`}>0{i+1}</div>
                <div className={`mt-2 text-sm font-medium ${active ? "text-ink" : "text-stone"}`}>{PHASE_LABELS[p]}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* NEXT UP */}
      {data.next_up && data.next_up.length > 0 && (
        <section className="border-y border-edge bg-bone">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-20">
            <div className="overline mb-4">Next up</div>
            <h2 className="font-display text-4xl tracking-tight">Here&apos;s what&apos;s on the table.</h2>
            <ul className="mt-12 border-t border-edge bg-linen" data-testid="microsite-next-up">
              {data.next_up.map((t, i) => (
                <li key={i} className="flex items-center gap-5 p-6 border-b border-edge">
                  <Circle className="w-5 h-5 text-edge flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-base">{t.title}</div>
                    <div className="overline mt-1">{PHASE_LABELS[t.phase]} · {t.assigned_role}</div>
                  </div>
                  <span className="font-display text-2xl text-stone">0{i+1}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* GALLERY */}
      {data.gallery && data.gallery.length > 0 && (
        <section className="border-y border-edge bg-linen">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
            <div className="overline mb-4">The home</div>
            <h2 className="font-display text-4xl tracking-tight">A few moments inside.</h2>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-px bg-edge border border-edge" data-testid="microsite-gallery-grid">
              {data.gallery.map((u, i) => (
                <div key={i} className="aspect-[4/3] bg-bone overflow-hidden">
                  <img src={absoluteUrl(u)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* COMPLETED MILESTONES */}
      {data.completed && data.completed.length > 0 && (
        <section className="max-w-[1100px] mx-auto px-6 lg:px-12 py-20">
          <div className="overline mb-4">Already done</div>
          <h2 className="font-display text-4xl tracking-tight">{data.completed.length} milestone{data.completed.length !== 1 ? "s" : ""} behind us.</h2>
          <ul className="mt-10 grid sm:grid-cols-2 gap-px bg-edge border border-edge" data-testid="microsite-completed">
            {data.completed.map((t, i) => (
              <li key={i} className="bg-linen p-5 flex items-start gap-3">
                <div className="w-5 h-5 bg-moss flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-linen" />
                </div>
                <div>
                  <div className="text-sm">{t.title}</div>
                  <div className="overline mt-1">{PHASE_LABELS[t.phase]}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* AGENT CARD */}
      <section className="border-t border-edge bg-moss text-linen">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-20 grid md:grid-cols-12 gap-10 items-center">
          {a.photo_url && (
            <div className="md:col-span-4">
              <div className="aspect-square overflow-hidden">
                <img src={absoluteUrl(a.photo_url)} alt={a.name} className="w-full h-full object-cover grayscale-[10%]" />
              </div>
            </div>
          )}
          <div className={a.photo_url ? "md:col-span-8" : "md:col-span-12"}>
            <div className="overline text-linen/70" data-testid="microsite-agent-overline">Your agent</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-3" data-testid="microsite-agent-name">{a.name}</h2>
            {a.tagline && <p className="mt-4 text-linen/80 leading-relaxed max-w-xl">{a.tagline}</p>}
            <div className="mt-8 flex flex-wrap gap-6 text-sm">
              {a.email && (
                <a href={`mailto:${a.email}`} className="flex items-center gap-2 hover:text-bone" data-testid="microsite-agent-email">
                  <Mail className="w-4 h-4" /> {a.email}
                </a>
              )}
              {a.phone && (
                <a href={`tel:${a.phone}`} className="flex items-center gap-2 hover:text-bone" data-testid="microsite-agent-phone">
                  <Phone className="w-4 h-4" /> {a.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="max-w-[1100px] mx-auto px-6 lg:px-12 py-8 text-xs text-stone flex items-center justify-between">
        <div>Made on Stoop — a quieter way to close.</div>
        <a href="/" className="overline hover:text-moss" data-testid="microsite-footer-link">stoop.studio</a>
      </footer>
    </div>
  );
}
